import { motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { customerMotionTransition } from '../customer-area'
import { formatCurrency, formatDateTime } from '../../helpers/formatters'
import type { CustomerOrderDetailLabels } from './orderDetailShared'
import { PaymentMethodLogo, resolvePaymentMethodVisualModel } from './paymentMethodVisual'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderPaymentSidebarSectionProps = {
  orderDetail: CustomerOrder
  language: ShopLanguage
  locale: ShopLocale
  labels: CustomerOrderDetailLabels
  reducedMotion: boolean
  supportRequestPath: string
  onNavigate: (path: string) => void
  t: TranslateFn
}

export const CustomerOrderPaymentSidebarSection = ({
  orderDetail,
  language,
  locale,
  labels,
  reducedMotion,
  supportRequestPath,
  onNavigate,
  t,
}: CustomerOrderPaymentSidebarSectionProps) => {
  const paymentMethodVisual = resolvePaymentMethodVisualModel(orderDetail, t('customer.orderDetail.paymentMethodFallback'))
  const shippingCost = Math.max(orderDetail.total - orderDetail.subtotal, 0)
  const paidOnDate = formatDateTime(orderDetail.paidAt || orderDetail.updatedAt || orderDetail.createdAt, locale, t('common.never'))
  const paidOnLabel = `${language === 'it' ? 'Pagato il' : 'Paid on'} ${paidOnDate}`

  return (
    <div className="space-y-4">
      <section className="ca-card-shell ca-theme-transition rounded-xl px-5 py-5">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-(--color-text-muted)">
          {t('customer.orders.total')}
        </p>
        <p className="mt-1 text-3xl font-semibold tracking-tight text-(--color-text-primary)">
          {formatCurrency(orderDetail.total, locale)}
        </p>

        <div className="mt-4 space-y-1.5 text-sm">
          <div className="flex items-center justify-between gap-3 text-(--color-text-secondary)">
            <span>{t('customer.orderDetail.subtotal')}</span>
            <span>{formatCurrency(orderDetail.subtotal, locale)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-(--color-text-secondary)">
            <span>{labels.shippingCost}</span>
            <span>{formatCurrency(shippingCost, locale)}</span>
          </div>
        </div>

        <div className="mt-4 border-t border-(--color-border-default) pt-3.5">
          <div className="flex items-center gap-3.5">
            <PaymentMethodLogo brand={paymentMethodVisual.brand} />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-(--color-text-primary)">{paymentMethodVisual.displayLine}</p>
              <p className="mt-0.5 text-xs text-(--color-text-muted)">{paidOnLabel}</p>
              <span className="sr-only">{paymentMethodVisual.label}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="ca-card-shell ca-theme-transition rounded-xl px-4 py-4">
        <h4 className="text-sm font-semibold text-(--color-text-primary)">{labels.supportAsideTitle}</h4>
        <p className="mt-1 text-xs text-(--color-text-secondary)">{labels.supportContextHint}</p>
        <motion.button
          type="button"
          onClick={() => onNavigate(supportRequestPath)}
          whileTap={reducedMotion ? undefined : { scale: 0.985 }}
          transition={customerMotionTransition}
          className="ca-btn-secondary mt-3 inline-flex h-9 w-full items-center justify-center px-3 text-sm font-semibold"
        >
          {labels.requestSupport}
        </motion.button>
      </section>
    </div>
  )
}
